import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clock, TrendingUp, TrendingDown, CheckCircle2, FileText, Users } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function ShiftsPage() {
    const [farm, setFarm] = useState({ currency: "PHP", name: "AJ's Farm" });
    const [shifts, setShifts] = useState([]);
    const [viewShift, setViewShift] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true); setError("");
        try {
            const [f, list] = await Promise.all([
                api.get("/farm").then(r => r.data),
                api.get("/pos/shifts").then(r => r.data),
            ]);
            setFarm(f);
            setShifts(list);
        } catch (requestError) {
            setError(requestError.response?.status === 403 ? "You are not authorized to view POS shifts." : "Shift records could not be loaded. Please try again.");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);
    const cur = (v) => formatCurrency(v, farm.currency);

    const openShift = shifts.find(s => s.status === "open");
    const closed = shifts.filter(s => s.status === "closed");
    const totalGross = closed.reduce((s, x) => s + Number(x.summary?.gross_sales || 0), 0);
    const totalVariance = closed.reduce((s, x) => s + Number(x.variance_cash || 0), 0);
    const shortCount = closed.filter(s => Number(s.variance_cash || 0) < -0.01).length;

    if (loading) return <div className="space-y-4" data-testid="shifts-page"><Card className="p-6 text-muted-foreground" data-testid="shifts-loading-state">{i18n.t("batch1.49b22df6e989")}</Card></div>;
    if (error) return <div className="space-y-4" data-testid="shifts-page"><Card className="p-4 border-destructive/40 text-destructive" role="alert" data-testid="shifts-error-state">{error}</Card><Button variant="outline" onClick={load}>{i18n.t("batch1.042c862e4467")}</Button></div>;

    const printZReport = (s) => {
        const doc = new jsPDF();
        doc.setFontSize(16); doc.setFont(undefined, "bold");
        doc.text("Z-REPORT", 14, 15);
        doc.setFontSize(10); doc.setFont(undefined, "normal");
        doc.text(farm.name || "Farm", 14, 22);
        doc.text(`Shift ID: ${s.id?.slice(0,8)}`, 14, 28);
        doc.text(`Cashier: ${s.cashier_name}`, 14, 34);
        doc.text(`Opened: ${new Date(s.opened_at).toLocaleString()}`, 14, 40);
        doc.text(`Closed: ${s.closed_at ? new Date(s.closed_at).toLocaleString() : "Still open"}`, 14, 46);
        const sum = s.summary || {};
        const byMethod = sum.by_method || {};
        const rows = [
            ["Opening Float", "", cur(s.opening_float || 0)],
            ["Invoices", String(sum.invoice_count || 0), ""],
            ["Gross Sales", "", cur(sum.gross_sales || 0)],
        ];
        Object.entries(byMethod).forEach(([m, agg]) => {
            rows.push([`  ${m.charAt(0).toUpperCase()+m.slice(1)}`, String(agg.count || 0), cur(agg.total || 0)]);
        });
        if (s.status === "closed") {
            rows.push(["Expected Cash", "", cur(s.expected_cash || 0)]);
            rows.push(["Counted Cash", "", cur(s.counted_cash || 0)]);
            rows.push(["VARIANCE", "", (Number(s.variance_cash||0) >= 0 ? "+" : "") + cur(s.variance_cash || 0)]);
        }
        autoTable(doc, { startY: 54, head: [["Item", "Count", "Amount"]], body: rows, styles: { fontSize: 10 }, headStyles: { fillColor: [64, 145, 108] } });
        if (s.closing_notes) {
            const y = doc.lastAutoTable.finalY + 8;
            doc.text("Notes:", 14, y);
            doc.text(s.closing_notes, 14, y + 5, { maxWidth: 180 });
        }
        doc.save(`z-report-${(s.id || "").slice(0,8)}.pdf`);
    };

    return (
        <div className="space-y-6" data-testid="shifts-page">
            <div>
                <div className="tiny-label">{i18n.t("batch1.da4c89e6e92c")}</div>
                <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch1.0c5bc1ae01ce")}</h1>
                <p className="text-muted-foreground mt-1">{i18n.t("batch1.f78768694337")}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-4"><div className="tiny-label">{i18n.t("batch1.31b1667fa635")}</div><div className="kpi-value text-2xl font-bold mt-2">{openShift ? 1 : 0}</div></Card>
                <Card className="p-4"><div className="tiny-label">{i18n.t("batch1.23d0c59c035a")}</div><div className="kpi-value text-2xl font-bold mt-2">{closed.length}</div></Card>
                <Card className="p-4"><div className="tiny-label">{i18n.t("batch1.6f9d99c836e1")}</div><div className="kpi-value text-2xl font-bold mt-2 font-mono">{cur(totalGross)}</div></Card>
                <Card className={`p-4 ${shortCount > 0 ? "bg-destructive/5" : ""}`}>
                    <div className={`tiny-label ${shortCount > 0 ? "text-destructive" : ""}`}>{i18n.t("batch1.c932ce9dd0d2")}</div>
                    <div className={`kpi-value text-2xl font-bold mt-2 ${shortCount > 0 ? "text-destructive" : ""}`}>{shortCount}</div>
                    {Math.abs(totalVariance) > 0.01 && <div className="text-xs font-mono mt-1">{i18n.t("batch1.9bb81c2eccbe")} {totalVariance < 0 ? "short " : "over "}{cur(Math.abs(totalVariance))}</div>}
                </Card>
            </div>

            <Card className="p-4">
                <Table>
                    <TableHeader><TableRow>
                        <TableHead>{i18n.t("batch1.78c072709400")}</TableHead>
                        <TableHead>{i18n.t("batch1.c4f1f5b1d49f")}</TableHead>
                        <TableHead>{i18n.t("batch1.88d86b7721d5")}</TableHead>
                        <TableHead className="text-right">{i18n.t("batch1.64435fd82de7")}</TableHead>
                        <TableHead className="text-right">{i18n.t("batch1.d0edfb6e8c55")}</TableHead>
                        <TableHead className="text-right">{i18n.t("batch1.eb39bbf6ccbb")}</TableHead>
                        <TableHead>{i18n.t("batch1.bae7d5be7082")}</TableHead>
                        <TableHead />
                    </TableRow></TableHeader>
                    <TableBody>
                        {shifts.map(s => {
                            const varN = Number(s.variance_cash || 0);
                            const varColor = s.status === "open" ? "text-muted-foreground"
                                : Math.abs(varN) < 0.01 ? "text-primary"
                                : varN < 0 ? "text-destructive" : "text-amber-600";
                            return (
                                <TableRow key={s.id} data-testid={`shift-row-${s.id}`}>
                                    <TableCell>
                                        <div className="flex items-center gap-2"><Users className="w-3 h-3 text-muted-foreground" /><span className="font-semibold">{s.cashier_name}</span></div>
                                    </TableCell>
                                    <TableCell className="text-xs font-mono">{new Date(s.opened_at).toLocaleString()}</TableCell>
                                    <TableCell className="text-xs font-mono">{s.closed_at ? new Date(s.closed_at).toLocaleString() : <span className="text-primary font-bold">{i18n.t("batch1.33906a7dfdec")}</span>}</TableCell>
                                    <TableCell className="text-right font-mono">{cur(s.opening_float)}</TableCell>
                                    <TableCell className="text-right font-mono">{cur(s.summary?.gross_sales || 0)}</TableCell>
                                    <TableCell className={`text-right font-mono font-semibold ${varColor}`}>
                                        {s.status === "closed" ? (
                                            <span className="inline-flex items-center gap-1 justify-end">
                                                {Math.abs(varN) < 0.01 ? <CheckCircle2 className="w-3 h-3" /> : varN < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                                                {(varN >= 0 ? "+" : "") + cur(varN)}
                                            </span>
                                        ) : "—"}
                                    </TableCell>
                                    <TableCell>
                                        {s.status === "open"
                                            ? <Badge className="bg-primary/10 text-primary border-primary/30"><Clock className="w-3 h-3 mr-1" />{i18n.t("batch1.33906a7dfdec")}</Badge>
                                            : <Badge variant="secondary">{i18n.t("batch1.be3e73b1165d")}</Badge>}
                                    </TableCell>
                                    <TableCell className="text-right space-x-1">
                                        <Button size="sm" variant="outline" onClick={() => setViewShift(s)} data-testid={`view-shift-${s.id}`}>{i18n.t("batch1.7c9a7c0610c1")}</Button>
                                        {s.status === "closed" && <Button size="sm" variant="outline" onClick={() => printZReport(s)} data-testid={`zreport-${s.id}`}><FileText className="w-3 h-3" /></Button>}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {shifts.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">{i18n.t("batch1.1e2c05f5bacd")}</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </Card>

            <Dialog open={!!viewShift} onOpenChange={(o) => !o && setViewShift(null)}>
                <DialogContent className="max-w-lg" data-testid="shift-detail-dialog">
                    <DialogHeader>
                        <DialogTitle>{i18n.t("batch1.2f905322013e")}</DialogTitle>
                        <DialogDescription>{i18n.t("batch1.9752ec5ddc51")}</DialogDescription>
                    </DialogHeader>
                    {viewShift && <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-2 gap-2">
                            <div><div className="tiny-label">{i18n.t("batch1.78c072709400")}</div><div className="font-semibold">{viewShift.cashier_name}</div></div>
                            <div><div className="tiny-label">{i18n.t("batch1.bae7d5be7082")}</div><div className="font-semibold uppercase">{viewShift.status}</div></div>
                            <div><div className="tiny-label">{i18n.t("batch1.c4f1f5b1d49f")}</div><div className="font-mono text-xs">{new Date(viewShift.opened_at).toLocaleString()}</div></div>
                            <div><div className="tiny-label">{i18n.t("batch1.88d86b7721d5")}</div><div className="font-mono text-xs">{viewShift.closed_at ? new Date(viewShift.closed_at).toLocaleString() : "—"}</div></div>
                        </div>
                        <div className="rounded-lg bg-secondary p-3 space-y-1">
                            <div className="flex justify-between"><span>{i18n.t("batch1.8c3ceb9d7a63")}</span><span className="font-mono">{cur(viewShift.opening_float)}</span></div>
                            <div className="flex justify-between"><span>{i18n.t("batch1.35f8f37b92ae")}</span><span className="font-mono">{viewShift.summary?.invoice_count || 0}</span></div>
                            <div className="flex justify-between font-bold"><span>{i18n.t("batch1.0fd159793490")}</span><span className="font-mono">{cur(viewShift.summary?.gross_sales || 0)}</span></div>
                        </div>
                        {viewShift.summary?.by_method && Object.keys(viewShift.summary.by_method).length > 0 && <div>
                            <div className="tiny-label mb-1">{i18n.t("batch1.b188ce60e7b0")}</div>
                            {Object.entries(viewShift.summary.by_method).map(([m, agg]) => (
                                <div key={m} className="flex justify-between text-xs py-1 border-b border-border last:border-0">
                                    <span className="capitalize">{m}</span>
                                    <span className="font-mono">{cur(agg.total)} ({agg.count})</span>
                                </div>
                            ))}
                        </div>}
                        {viewShift.status === "closed" && <div className={`rounded-lg p-3 ${Number(viewShift.variance_cash||0) === 0 ? "bg-primary/5 border-primary/40" : Number(viewShift.variance_cash||0) < 0 ? "bg-destructive/5 border-destructive/40" : "bg-amber-500/5 border-amber-500/40"} border`}>
                            <div className="flex justify-between"><span>{i18n.t("batch1.06ad8947fca5")}</span><span className="font-mono">{cur(viewShift.expected_cash || 0)}</span></div>
                            <div className="flex justify-between"><span>{i18n.t("batch1.a63f8fcba6a4")}</span><span className="font-mono">{cur(viewShift.counted_cash || 0)}</span></div>
                            <div className="flex justify-between font-bold pt-1 border-t border-border/50 mt-1"><span>{i18n.t("batch1.eb39bbf6ccbb")}</span><span className="font-mono">{(Number(viewShift.variance_cash||0) >= 0 ? "+" : "") + cur(viewShift.variance_cash || 0)}</span></div>
                        </div>}
                        {viewShift.closing_notes && <div><div className="tiny-label">{i18n.t("batch1.70440046a3dc")}</div><div className="text-xs bg-secondary rounded p-2">{viewShift.closing_notes}</div></div>}
                        {viewShift.status === "closed" && <Button onClick={() => printZReport(viewShift)} className="w-full h-11"><FileText className="w-4 h-4 mr-2" />{i18n.t("batch1.b5ea57b10f2e")}</Button>}
                    </div>}
                </DialogContent>
            </Dialog>
        </div>
    );
}
